import React from 'react';
import {Stack, Typography} from '@mui/material';
import {format, parse} from 'date-fns';
import {ptBR} from 'date-fns/locale';

import {AlertInfo, AlertModal, TagChip} from 'presentation/components';
import {Drawer} from 'presentation/components/Drawer';
import {translations} from 'presentation/translations-new';
import {formatPriceBill} from 'presentation/utils';

import {BeneficiaryModel} from 'domain/usecases/others/selection-flow/beneficiary-v3';
import {useGetBeneficiaries} from 'presentation/pages/others/selection-flow/queries/useGetBeneficiaries';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {useHostName} from 'presentation/hooks/useHostName';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {FeaturesEnum} from 'presentation/constants';
import {useDetailsDrawer} from './hooks/useDetailsDrawer';
import {DrawerAlertInfo, ChargeDetailsAccordion} from './styles';
import {PlanAdjustmentLinkEnum, type DetailsDrawerProps} from './types';
import {CoparticipationTooltipCard} from './components/CoparticipationTooltipCard';
import {ChargeDetailsAccordionContent} from './components/ChargeDetailsAccordionContent';
import {DetailsDrawerInfoItem} from './components/DetailsDrawerInfoItem';
import {OpenBillStatusEnum} from '../OpenBillsTable/types';

const {invoiceDetailsPage} = translations['pt-br'].privateFlow.myFinancial;
const {close, notice} = translations['pt-br'].commons;
const {
	openBills: {errorDownloadPdf},
} = translations['pt-br'].privateFlow.myFinancial;
const {
	invoiceReference,
	contractNumber,
	competenceOfFee,
	beneficiaries,
	dueDate,
	invoiceValue,
	chargeDetails,
	warningTitle,
	paymentIdentificationWarning,
	autoPaymentAlert,
	payInvoice,
	downloadInvoice,
	nothingToShow,
	planAdjustmentAlert,
} = translations['pt-br'].privateFlow.myFinancial.openBills.detailsDrawer;

function DetailsDrawer({
	open,
	onClose,
	row,
	onPayBill,
	hasAutoPayment,
	isPayLoading,
}: DetailsDrawerProps) {
	const {
		isMobile,
		tooltipOpen,
		tooltipAnchorEl,
		isOpenBillWithAutoPayment,
		handleInfoIconClick,
		handleTooltipClose,
		isDownloading,
		modalErrorState,
		handleDownload,
		handleCloseErrorModal,
	} = useDetailsDrawer({open, row, hasAutoPayment});
	const {beneficiary} = useAccountDataStore(state => state.accountData);
	const {isClinipam, isCCG, isGndiMinas, isGndiSaoPaulo} = useHostName();
	const {isEnabled: isPlanValueUpdateEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BILLS_PLAN_VALUE_UPDATE,
	);
	const nuContrato = beneficiary.nuContratoC ?? beneficiary.nuContrato;

	const planAdjustmentLink = (() => {
		if (isGndiSaoPaulo) return PlanAdjustmentLinkEnum.GNDI;
		if (isClinipam) return PlanAdjustmentLinkEnum.CLINIPAM;
		if (isGndiMinas) return PlanAdjustmentLinkEnum.GNDI_MINAS;
		if (isCCG) return PlanAdjustmentLinkEnum.CCG;
		return PlanAdjustmentLinkEnum.HAP;
	})();
	const {data: beneficiariesList} = useGetBeneficiaries({nuContrato: String(nuContrato ?? '')});
	const uniqueBeneficiaries = new Set();
	const filteredBeneficiaries = beneficiariesList
		? beneficiariesList.filter(
				(beneficiary: BeneficiaryModel) =>
					!uniqueBeneficiaries.has(beneficiary.cdUsuarioC) &&
					uniqueBeneficiaries.add(beneficiary.cdUsuarioC),
			)
		: null;

	const saoLucasDownloadLink = row?.isSaoLucas ? row.downloadLink : undefined;
	const isHideButtonPayment = row?.cdEspecieTitulo === 70;

	return (
		<Drawer.Root
			open={open}
			onClose={onClose}
			onOpen={() => {}}
			width="639px"
			customMaxHeight="100%"
			pullerPosition="absolute">
			<Drawer.Header containerPadding={'16px'} title={invoiceDetailsPage} onClose={onClose} />
			<Drawer.Content>
				{row?.isSaoLucas ? (
					<Stack>
						<Stack width="fit-content" mb={3}>
							<TagChip
								label={row.status}
								color={row.status === OpenBillStatusEnum.OVERDUE ? 'error' : 'info'}
							/>
						</Stack>
						<Stack gap={3}>
							<Stack direction={isMobile ? 'column' : 'row'} gap={2}>
								<Stack flex={1}>
									<DetailsDrawerInfoItem
										iconName="calendar-clock"
										primary={dueDate}
										secondary={row.dtVencimento}
									/>
								</Stack>
								<Stack flex={1}>
									<DetailsDrawerInfoItem
										iconName="receipt-long-filled"
										primary={invoiceValue}
										secondary={`${formatPriceBill(row.status === OpenBillStatusEnum.OVERDUE ? (row.vlAtualizado ?? row.vlObrigacao) : row.vlObrigacao)}${row.status === OpenBillStatusEnum.OVERDUE ? '*' : ''}`}
									/>
								</Stack>
							</Stack>
						</Stack>
					</Stack>
				) : null}
				{row && !row.isSaoLucas ? (
					<Stack>
						<Stack width="fit-content" mb={3}>
							<TagChip
								label={row.status}
								color={row.status === OpenBillStatusEnum.OVERDUE ? 'error' : 'info'}
							/>
						</Stack>
						<Stack gap={3}>
							<Stack direction={isMobile ? 'column' : 'row'} gap={2}>
								<Stack flex={1}>
									<DetailsDrawerInfoItem
										iconName="event-filled"
										primary={invoiceReference}
										secondary={format(
											parse(row.dtVencimento, 'dd/MM/yyyy', new Date()),
											'MMM/yyyy',
											{locale: ptBR},
										).replace(/^\w/, c => c.toUpperCase())}
									/>
								</Stack>
								<Stack flex={1}>
									<DetailsDrawerInfoItem
										iconName="hapvida-logo"
										primary={contractNumber}
										secondary={String(
											beneficiary.nuContratoC ?? beneficiary.nuContrato ?? '',
										)}
									/>
								</Stack>
							</Stack>
							<Stack direction={isMobile ? 'column' : 'row'} gap={2}>
								<Stack flex={1}>
									<DetailsDrawerInfoItem
										iconName="calendar-range"
										primary={competenceOfFee}
										secondary={row.competencia ?? row.dtPrevista}
									/>
								</Stack>
								<Stack flex={1}>
									<DetailsDrawerInfoItem
										iconName="person-filled"
										primary={beneficiaries}
										secondary={
											filteredBeneficiaries
												? String(filteredBeneficiaries.length).padStart(
														2,
														'0',
													)
												: String(row.beneficiarios ?? 0).padStart(2, '0')
										}
									/>
								</Stack>
							</Stack>
							<Stack direction={isMobile ? 'column' : 'row'} gap={2}>
								<Stack flex={1}>
									<DetailsDrawerInfoItem
										iconName="calendar-clock"
										primary={dueDate}
										secondary={row.dtVencimento}
									/>
								</Stack>
								<Stack flex={1}>
									<DetailsDrawerInfoItem
										iconName="receipt-long-filled"
										primary={invoiceValue}
										secondary={`${formatPriceBill(row.status === OpenBillStatusEnum.OVERDUE ? (row.vlAtualizado ?? row.vlObrigacao) : row.vlObrigacao)}`}
									/>
								</Stack>
							</Stack>
						</Stack>
						{row?.status === OpenBillStatusEnum.OVERDUE ? (
							<DrawerAlertInfo severity="warning">
								<AlertInfo.Title title={warningTitle} />
								<AlertInfo.Description>
									{paymentIdentificationWarning}
								</AlertInfo.Description>
							</DrawerAlertInfo>
						) : null}

						{isOpenBillWithAutoPayment ? (
							<DrawerAlertInfo severity="info">
								<AlertInfo.Title title={warningTitle} />
								<AlertInfo.Description>{autoPaymentAlert}</AlertInfo.Description>
							</DrawerAlertInfo>
						) : null}
						<ChargeDetailsAccordion title={chargeDetails}>
							{row.coparticipacao?.length && row.baseYear != null ? (
								<ChargeDetailsAccordionContent
									billingBeneficiaries={row.coparticipacao}
									totalValue={row.vlAtualizado}
									discountAmount={row.extraParams?.discountAmount ?? '0'}
									fineAmount={row.extraParams?.fineAmount ?? '0'}
									interestAmount={row.extraParams?.interestAmount ?? '0'}
									onInfoIconClick={handleInfoIconClick}
								/>
							) : (
								<Typography variant="body2" color="text.secondary">
									{nothingToShow}
								</Typography>
							)}
						</ChargeDetailsAccordion>
						{isPlanValueUpdateEnabled ? (
							<DrawerAlertInfo severity="info">
								<AlertInfo.Title title={planAdjustmentAlert.title} />
								<AlertInfo.Description>
									{planAdjustmentAlert.description}
								</AlertInfo.Description>
								<Stack>
								<AlertInfo.Action
									onClick={() => window.open(planAdjustmentLink, '_blank', 'noopener,noreferrer')}>
										{planAdjustmentAlert.action}
									</AlertInfo.Action>
								</Stack>
							</DrawerAlertInfo>
						) : null}
					</Stack>
				) : null}
			</Drawer.Content>
			{saoLucasDownloadLink ? (
				<Drawer.Footer
					hideDivider
					hideSecondButton
					firstButtonTitle={downloadInvoice}
					firstButtonOnClick={() => window.open(saoLucasDownloadLink, '_blank')}
				/>
			) : null}
			{row && !row.isSaoLucas ? (
				<Drawer.Footer
					canStacks
					direction="row-reverse"
					firstButtonTitle={downloadInvoice}
					firstButtonOnClick={handleDownload}
					firstButtonIsLoading={isDownloading}
					firstButtonDisabled={isDownloading}
					hideSecondButton={isHideButtonPayment}
					secondButtonTitle={payInvoice}
					secondButtonOnClick={onPayBill}
					secondButtonIsLoading={isPayLoading}
					secondButtonDisabled={isPayLoading}
				/>
			) : null}
			<AlertModal
				open={modalErrorState}
				onClose={handleCloseErrorModal}
				headerTitle={notice}
				variant="info"
				title={errorDownloadPdf.title}
				description={errorDownloadPdf.description}
				buttonLabel={close}
			/>
			<CoparticipationTooltipCard
				anchorEl={tooltipAnchorEl}
				open={tooltipOpen}
				onClose={handleTooltipClose}
			/>
		</Drawer.Root>
	);
}

export {DetailsDrawer};
